import { useEffect } from 'react';
import { supabase } from '../../config/supabase';
import { useStore } from '../../store/useStore';
import { logger } from '../../utils/logger';
import toast from 'react-hot-toast';

export const useRealtimeSync = () => {
  const store = useStore();

  useEffect(() => {
    if (!store.currentUser) return;

    logger.info('Setting up realtime subscriptions');

    // Message acknowledgments subscription with improved sync
    const acknowledgementsChannel = supabase
      .channel('acknowledgments_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_acknowledgments'
        },
        async (payload) => {
          logger.info('Acknowledgment change received:', payload);

          try {
            if (payload.eventType === 'INSERT') {
              // Fetch complete message data with all relations
              const { data: message, error: messageError } = await supabase
                .from('messages')
                .select(`
                  *,
                  sender:profiles!messages_sender_id_fkey (
                    id,
                    name,
                    email,
                    role,
                    department,
                    avatar
                  ),
                  acknowledgments:message_acknowledgments (
                    id,
                    user_id,
                    acknowledged_at,
                    user:profiles(*)
                  ),
                  recipients:message_recipients (
                    id,
                    user_id,
                    user:profiles(*)
                  )
                `)
                .eq('id', payload.new.message_id)
                .single();

              if (messageError) {
                logger.error('Error fetching message data:', messageError);
                throw messageError;
              }

              if (!message) {
                logger.error('No message found for acknowledgment');
                return;
              }

              // Update store with complete message data
              store.editMessage(message.id, {
                ...message,
                createdAt: new Date(message.created_at),
                updatedAt: new Date(message.updated_at),
                scheduledFor: message.scheduled_for ? new Date(message.scheduled_for) : undefined,
                acknowledgments: message.acknowledgments.map(ack => ack.user_id),
                recipients: message.recipients.map(rec => ({
                  id: rec.user.id,
                  name: rec.user.name,
                  email: rec.user.email,
                  role: rec.user.role,
                  department: rec.user.department,
                  avatar: rec.user.avatar
                }))
              });

              // Show notification for message sender
              if (message.sender_id === store.currentUser.id) {
                const { data: user } = await supabase
                  .from('profiles')
                  .select('name')
                  .eq('id', payload.new.user_id)
                  .single();

                toast.success(`${user?.name || 'Someone'} acknowledged your message`);
              }

              // If this is a task-related message, update task acknowledgment
              if (message.type === 'DIRECT' && message.title.includes('Task')) {
                const taskId = message.title.split(':')[1]?.trim();
                if (taskId) {
                  const { error: taskError } = await supabase
                    .from('tasks')
                    .update({
                      acknowledged: true,
                      acknowledged_at: new Date().toISOString(),
                      updated_at: new Date().toISOString()
                    })
                    .eq('id', taskId);

                  if (taskError) {
                    logger.error('Error updating task acknowledgment:', taskError);
                  }
                }
              }
            }
          } catch (error) {
            logger.error('Error processing acknowledgment change:', error);
          }
        }
      )
      .subscribe();

    // Messages subscription
    const messagesChannel = supabase
      .channel('messages_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages'
        },
        async (payload) => {
          logger.info('Messages change received:', payload);

          try {
            // Fetch complete message data
            const { data: message, error } = await supabase
              .from('messages')
              .select(`
                *,
                sender:profiles!messages_sender_id_fkey (
                  id,
                  name,
                  email,
                  role,
                  department,
                  avatar
                ),
                acknowledgments:message_acknowledgments (
                  id,
                  user_id,
                  acknowledged_at,
                  user:profiles(*)
                ),
                recipients:message_recipients (
                  id,
                  user_id,
                  user:profiles(*)
                )
              `)
              .eq('id', payload.new.id)
              .single();

            if (error) throw error;

            // Update store based on event type
            switch (payload.eventType) {
              case 'INSERT':
                store.addMessage({
                  ...message,
                  createdAt: new Date(message.created_at),
                  updatedAt: new Date(message.updated_at),
                  scheduledFor: message.scheduled_for ? new Date(message.scheduled_for) : undefined,
                  acknowledgments: message.acknowledgments.map(ack => ack.user_id),
                  recipients: message.recipients.map(rec => ({
                    id: rec.user.id,
                    name: rec.user.name,
                    email: rec.user.email,
                    role: rec.user.role,
                    department: rec.user.department,
                    avatar: rec.user.avatar
                  }))
                });
                break;
              case 'UPDATE':
                store.editMessage(message.id, {
                  ...message,
                  createdAt: new Date(message.created_at),
                  updatedAt: new Date(message.updated_at),
                  scheduledFor: message.scheduled_for ? new Date(message.scheduled_for) : undefined,
                  acknowledgments: message.acknowledgments.map(ack => ack.user_id),
                  recipients: message.recipients.map(rec => ({
                    id: rec.user.id,
                    name: rec.user.name,
                    email: rec.user.email,
                    role: rec.user.role,
                    department: rec.user.department,
                    avatar: rec.user.avatar
                  }))
                });
                break;
              case 'DELETE':
                store.archiveMessage(message.id);
                break;
            }
          } catch (error) {
            logger.error('Error processing message change:', error);
          }
        }
      )
      .subscribe();

    // Cleanup subscriptions on unmount
    return () => {
      acknowledgementsChannel.unsubscribe();
      messagesChannel.unsubscribe();
    };
  }, [store.currentUser]);
};